export type MainDrum = "kick" | "snare" | "hat";
export type GroovePattern = Record<MainDrum, boolean[]>;
// Must match GrooveStudioJrSavedGroove.TITLE_MAX_LENGTH on the server.
export const GROOVE_TITLE_MAX_LENGTH = 150;
export type BassRhythm = "follow-kick" | "quarters" | "eighths" | "sixteenths";
export type BassStyle = BassRhythm | "random";
// Key values must match the bass-key validation in the Django game API.
export const BASS_KEYS = [
  { value: "C", label: "C", rootMidi: 36 },
  { value: "C#", label: "C♯", rootMidi: 37 },
  { value: "D", label: "D", rootMidi: 38 },
  { value: "Eb", label: "E♭", rootMidi: 39 },
  { value: "E", label: "E", rootMidi: 28 },
  { value: "F", label: "F", rootMidi: 29 },
  { value: "F#", label: "F♯", rootMidi: 30 },
  { value: "G", label: "G", rootMidi: 31 },
  { value: "Ab", label: "A♭", rootMidi: 32 },
  { value: "A", label: "A", rootMidi: 33 },
  { value: "Bb", label: "B♭", rootMidi: 34 },
  { value: "B", label: "B", rootMidi: 35 },
] as const;
export type BassKey = (typeof BASS_KEYS)[number]["value"];
export type BassScale = "minor" | "major";
export type BassMelody = "pentatonic" | "progression";
// Must match progression IDs in the Django game API.
export const BASS_PROGRESSION_IDS = [
  "default",
  "pop",
  "fifties",
  "journey",
  "rock",
  "minor",
  "bright",
  "soft",
  "descending",
  "u2",
  "three",
] as const;
export type BassProgression = (typeof BASS_PROGRESSION_IDS)[number];
export interface BassSettings {
  enabled: boolean;
  style: BassStyle;
  volume: number;
  key: BassKey;
  scale: BassScale;
  melody: BassMelody;
  progression: BassProgression;
}
export type DrumKit = "rock" | "electronic" | "mr-elias";
export type HiHatFeel = "even" | "eighth" | "quarter";
export interface GrooveConfiguration {
  tempo: number;
  meter: 3 | 4;
  subdivision: 2 | 4;
  hi_hat_feel: HiHatFeel;
  drum_kit: DrumKit;
  measures: GroovePattern[];
  bass: BassSettings;
}
export interface SavedGroove {
  id: number;
  title: string;
  configuration: GrooveConfiguration;
}

export function isGrooveConfiguration(
  value: unknown,
): value is GrooveConfiguration {
  // Must match valid_configuration in project/music_games/api/groove_studio_jr.py.
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const config = value as Record<string, unknown>;
  if (
    Object.keys(config).sort().join(",") !==
    "bass,drum_kit,hi_hat_feel,measures,meter,subdivision,tempo"
  )
    return false;
  const bass = config.bass as Record<string, unknown> | null;
  if (
    !bass ||
    typeof bass !== "object" ||
    Array.isArray(bass) ||
    Object.keys(bass).sort().join(",") !==
      "enabled,key,melody,progression,scale,style,volume" ||
    typeof bass.enabled !== "boolean" ||
    !["follow-kick", "quarters", "eighths", "sixteenths", "random"].includes(
      bass.style as string,
    ) ||
    !Number.isInteger(bass.volume) ||
    (bass.volume as number) < 0 ||
    (bass.volume as number) > 100 ||
    !BASS_KEYS.some((key) => key.value === bass.key) ||
    !["minor", "major"].includes(bass.scale as string) ||
    !["pentatonic", "progression"].includes(bass.melody as string) ||
    !BASS_PROGRESSION_IDS.includes(bass.progression as BassProgression)
  )
    return false;
  if (
    !Number.isInteger(config.tempo) ||
    (config.tempo as number) < 50 ||
    (config.tempo as number) > 180 ||
    ![3, 4].includes(config.meter as number) ||
    ![2, 4].includes(config.subdivision as number) ||
    !["even", "eighth", "quarter"].includes(config.hi_hat_feel as string) ||
    !["rock", "electronic", "mr-elias"].includes(config.drum_kit as string) ||
    !Array.isArray(config.measures) ||
    config.measures.length < 1 ||
    config.measures.length > 8
  )
    return false;
  return config.measures.every((measure: unknown) => {
    if (!measure || typeof measure !== "object" || Array.isArray(measure))
      return false;
    if (Object.keys(measure).sort().join(",") !== "hat,kick,snare")
      return false;
    return Object.values(measure).every(
      (hits) =>
        Array.isArray(hits) &&
        hits.length ===
          (config.meter as number) * (config.subdivision as number) &&
        hits.every((hit) => typeof hit === "boolean"),
    );
  });
}
export type ChallengeDef = {
  meter?: 3 | 4;
  measureCount?: 1 | 2;
  subdivision?: 2 | 4;
  title: string;
  level: number;
  instruction: string;
  bpm: number;
  kick: number[];
  snare: number[];
  hat: number[];
};
// Must match LEVEL_ONE_PATTERNS in project/music_games/api/groove_studio_jr.py.
const allEighths = [0, 1, 2, 3, 4, 5, 6, 7];
const backbeat = [2, 6];
export const LEVEL_ONE_PATTERNS: ChallengeDef[] = [
  {
    title: "Add the Snare",
    level: 1,
    instruction: "Find the bass drum and snare beats.",
    bpm: 85,
    kick: [0, 4],
    snare: backbeat,
    hat: [],
  },
  {
    title: "Keep the Pulse",
    level: 1,
    instruction: "Add the quarter-note hi-hat pulse.",
    bpm: 85,
    kick: [0, 4],
    snare: backbeat,
    hat: [0, 2, 4, 6],
  },
  {
    title: "Eighth-Note Energy",
    level: 1,
    instruction: "Listen for the hi-hat on every eighth note.",
    bpm: 85,
    kick: [0, 4],
    snare: backbeat,
    hat: allEighths,
  },
  {
    title: "Four on the Floor",
    level: 1,
    instruction: "The bass drum plays on every beat.",
    bpm: 85,
    kick: [0, 2, 4, 6],
    snare: backbeat,
    hat: allEighths,
  },
  {
    title: "Extra Kick",
    level: 1,
    instruction: "Hear the extra kick on the & of beat 3.",
    bpm: 85,
    kick: [0, 4, 5],
    snare: backbeat,
    hat: allEighths,
  },
  {
    title: "Double Kick",
    level: 1,
    instruction: "Find the extra kick on the & of beat 1.",
    bpm: 85,
    kick: [0, 1, 4],
    snare: backbeat,
    hat: allEighths,
  },
  {
    title: "Double Kick Plus",
    level: 1,
    instruction: "Listen for kicks on 1, 1&, 3, and 3&.",
    bpm: 85,
    kick: [0, 1, 4, 5],
    snare: backbeat,
    hat: allEighths,
  },
  {
    title: "Offbeat Hi-Hats",
    level: 1,
    instruction: "The hi-hat plays only on the & counts.",
    bpm: 85,
    kick: [0, 4],
    snare: backbeat,
    hat: [1, 3, 5, 7],
  },
  {
    title: "Offbeat Bass & Steady Hats",
    level: 1,
    instruction:
      "Kick on every &, snare on every beat, and hi-hat on beats 1, 2, 3, and 4.",
    bpm: 85,
    kick: [1, 3, 5, 7],
    snare: [0, 2, 4, 6],
    hat: [0, 2, 4, 6],
  },
  {
    title: "Offbeat Bass",
    level: 1,
    instruction: "The kick plays on every &, with snare on every beat.",
    bpm: 85,
    kick: [1, 3, 5, 7],
    snare: [0, 2, 4, 6],
    hat: allEighths,
  },
];

// Each pair joins two Level 1 grooves (one-based numbers) into one groove.
// Must match LEVEL_TWO_PAIRS and CHALLENGE_PATTERNS in the Django game API.
export const LEVEL_TWO_PAIRS = [
  [1, 2],
  [3, 3],
  [3, 4],
  [3, 5],
  [3, 6],
  [5, 6],
  [3, 7],
  [3, 8],
  [7, 8],
  [9, 10],
] as const;
export const LEVEL_TWO_PATTERNS: ChallengeDef[] = LEVEL_TWO_PAIRS.map(
  ([first, second]) => {
    const a = LEVEL_ONE_PATTERNS[first - 1],
      b = LEVEL_ONE_PATTERNS[second - 1];
    return {
      title: `${a.title} / ${b.title}`,
      instruction: "Listen to both measures, then copy the complete groove.",
      level: 2,
      bpm: 85,
      kick: [...a.kick, ...b.kick.map((i) => i + 8)],
      snare: [...a.snare, ...b.snare.map((i) => i + 8)],
      hat: [...a.hat, ...b.hat.map((i) => i + 8)],
    };
  },
);
// Must match LEVEL_THREE_PATTERNS in the Django game API.
export const LEVEL_THREE_PATTERNS: ChallengeDef[] = [
  { kick: [0], snare: [6] },
  { kick: [0, 10], snare: [6] },
  { kick: [0, 2], snare: [6] },
  { kick: [0, 6], snare: [6], title: "Kick on Both Downbeats" },
  { kick: [0, 4], snare: [6] },
  { kick: [0, 2, 10], snare: [6] },
  { kick: [0, 4, 10], snare: [6] },
  { kick: [0, 2, 4, 10], snare: [6] },
  { kick: [0, 2, 4], snare: [6, 10] },
  { kick: [0, 2, 4], snare: [6, 8, 10] },
].map((pattern, index) => ({
  title: `Three-beat phrase ${index + 1}`,
  ...pattern,
  instruction:
    "Listen to both measures of 3/4, then copy the quarter-note groove.",
  level: 3,
  bpm: 110,
  meter: 3,
  measureCount: 2,
  hat: [0, 2, 4, 6, 8, 10],
}));
// Must match LEVEL_FOUR_PATTERNS in the Django game API.
export const LEVEL_FOUR_PATTERNS: ChallengeDef[] = [
  { title: "Eighth-Note Foundation", kick: [0], snare: [6] },
  { title: "Early Extra Kick", kick: [0, 1], snare: [6] },
  { title: "Kick into the Snare", kick: [0, 5], snare: [6] },
  { title: "Kick into the Repeat", kick: [0, 11], snare: [6] },
  { title: "Both Kick Pickups", kick: [0, 5, 11], snare: [6] },
  { title: "Extra Snare", kick: [0], snare: [6, 7] },
  { title: "Kick and Snare Variation", kick: [0, 5], snare: [6, 7] },
  { title: "Offbeat Hi-Hats", kick: [0], snare: [6], hat: [1, 3, 5, 7, 9, 11] },
  {
    title: "Offbeat Hats and Kick Pickup",
    kick: [0, 5],
    snare: [6],
    hat: [1, 3, 5, 7, 9, 11],
  },
  {
    title: "Final Combination",
    kick: [0, 5, 11],
    snare: [6, 7],
    hat: [0, 1, 2, 3, 4, 5, 7, 9, 11],
  },
].map((pattern) => ({
  ...pattern,
  hat: pattern.hat ?? Array.from({ length: 12 }, (_, i) => i),
  instruction:
    "Listen to both measures of 3/4, then copy the eighth-note groove.",
  level: 4,
  bpm: 85,
  meter: 3,
  measureCount: 2,
}));
// Must match LEVEL_FIVE_PATTERNS in the Django game API.
export const LEVEL_FIVE_PATTERNS: ChallengeDef[] = [
  { title: "Sixteenth-Note Foundation", kick: [0, 8] },
  { title: "Four on the Floor", kick: [0, 4, 8, 12] },
  { title: "Kick on 1 a", kick: [0, 3, 8] },
  { title: "Kick on 3 a", kick: [0, 8, 11] },
  { title: "Both Kick Pickups", kick: [0, 3, 8, 11] },
  { title: "Kick on 2 &", kick: [0, 6, 8] },
  { title: "Kick on 3 e", kick: [0, 8, 9] },
  { title: "Kick into the Repeat", kick: [0, 8, 15] },
  { title: "Extra Snare", kick: [0, 8], snare: [4, 12, 15] },
  {
    title: "Kick Pickups and Snare Turnaround",
    kick: [0, 3, 8, 11],
    snare: [4, 12, 15],
  },
].map((pattern) => ({
  ...pattern,
  snare: pattern.snare ?? [4, 12],
  hat: Array.from({ length: 16 }, (_, i) => i),
  instruction:
    "Listen to the sixteenth-note groove, then copy the kick and snare with steady hi-hats.",
  level: 5,
  bpm: 75,
  meter: 4,
  subdivision: 4,
  measureCount: 1,
}));
// Must match LEVEL_SIX_PATTERNS in the Django game API.
const levelSixHatPhrases = [
  [0, 2, 3],
  [0, 1, 2],
  [0, 1, 3],
  [0, 3],
  [0, 1],
];
const levelSixExtraKicks = [
  [0, 3, 8],
  [0, 8, 11],
  [0, 6, 8],
  [0, 8, 9],
  [0, 3, 8, 11],
];
export const LEVEL_SIX_PATTERNS: ChallengeDef[] = Array.from(
  { length: 10 },
  (_, index) => ({
    title: `Broken Hi-Hats ${index + 1}`,
    instruction:
      "Repeat the same hi-hat phrase on every beat and copy the kick and snare.",
    level: 6,
    bpm: index < 5 ? 70 : 60,
    meter: 4,
    subdivision: 4,
    measureCount: 1,
    kick: index < 5 ? [0, 8] : levelSixExtraKicks[index - 5],
    snare: [4, 12],
    hat: Array.from({ length: 4 }, (_, beat) =>
      levelSixHatPhrases[index % 5].map((step) => beat * 4 + step),
    ).flat(),
  }),
);
// Must match LEVEL_SEVEN_PATTERNS in the Django game API.
export const LEVEL_SEVEN_PATTERNS: ChallengeDef[] = [
  { kick: [0, 4, 8], snare: [2, 12, 14], hat: [0, 1, 2, 4, 6, 8, 10, 12, 14] },
  {
    kick: [1, 4, 8],
    snare: [0, 3, 9, 10, 12, 14],
    hat: [0, 2, 4, 6, 8, 10, 12, 14],
  },
  { kick: [0, 8], snare: [4, 12], hat: [1, 3, 5, 7, 9, 11, 13, 15] },
  { kick: [0, 2, 8], snare: [4, 12], hat: [0, 1, 4, 5, 8, 9, 12, 13] },
  {
    kick: [1, 2, 5, 9, 10, 13],
    snare: [4, 12],
    hat: [1, 2, 5, 6, 9, 10, 13, 14],
  },
  {
    kick: [2, 5, 10, 11, 13],
    snare: [4, 12],
    hat: [2, 3, 6, 7, 10, 11, 14, 15],
  },
  {
    kick: [0, 2, 8, 10, 13],
    snare: [4, 12],
    hat: [2, 3, 5, 6, 10, 11, 13, 14],
  },
  {
    kick: [2, 4, 10, 12, 13],
    snare: [3, 11],
    hat: [0, 2, 5, 6, 8, 10, 13, 14],
  },
  { kick: [0, 5, 13, 14], snare: [3, 4, 7, 12], hat: [0, 1, 4, 8, 11, 12] },
  { kick: [0, 5, 13, 14], snare: [1, 3, 6, 11, 15], hat: [0, 4, 8, 11, 14] },
].map((pattern, index) => ({
  ...pattern,
  title: `Syncopation ${index + 1}`,
  instruction: "Listen closely and copy the complete syncopated groove.",
  level: 7,
  bpm: 100,
  meter: 4,
  subdivision: 4,
  measureCount: 1,
}));
export function challengeStepCount(challenge: ChallengeDef): number {
  return (
    (challenge.meter ?? 4) *
    (challenge.subdivision ?? 2) *
    (challenge.measureCount ?? (challenge.level === 2 ? 2 : 1))
  );
}
// The grooves in the order students play them. A groove's ID (used by the
// Django game API) is its index plus one.
export const CHALLENGE_PATTERNS = [
  ...LEVEL_ONE_PATTERNS,
  ...LEVEL_TWO_PATTERNS,
  ...LEVEL_THREE_PATTERNS,
  ...LEVEL_FOUR_PATTERNS,
  ...LEVEL_FIVE_PATTERNS,
  ...LEVEL_SIX_PATTERNS,
  ...LEVEL_SEVEN_PATTERNS,
];
export function levelChallenges(level: number): number[] {
  return CHALLENGE_PATTERNS.flatMap((challenge, index) =>
    challenge.level === level ? [index] : [],
  );
}
// The groove's number within its level, starting at 1
export function challengeDisplayNumber(index: number): number {
  return levelChallenges(CHALLENGE_PATTERNS[index].level).indexOf(index) + 1;
}

export function compareGroove(index: number, answer: GroovePattern) {
  const expected = CHALLENGE_PATTERNS[index];
  let missing = 0,
    extra = 0;
  for (const drum of ["kick", "snare", "hat"] as MainDrum[]) {
    for (let i = 0; i < challengeStepCount(expected); i++) {
      if (expected[drum].includes(i) && !answer[drum][i]) missing++;
      if (!expected[drum].includes(i) && answer[drum][i]) extra++;
    }
  }
  return { missing, extra };
}

// Each groove unlocks the next one, including the first groove of the next
// level
export function isChallengeUnlocked(index: number, completed: number[]) {
  if (index < 0 || index >= CHALLENGE_PATTERNS.length) return false;
  return (
    index === 0 || completed.includes(index) || completed.includes(index - 1)
  );
}
export const CHALLENGE_LEVEL_COUNT = 7;
export function isLevelUnlocked(level: number, completed: number[]) {
  if (level < 1 || level > CHALLENGE_LEVEL_COUNT) return false;
  if (level === 1) return true;
  return completed.includes(levelChallenges(level - 1).pop() as number);
}

export type GroovePreset =
  | "rock"
  | "pop"
  | "dance"
  | "halfTime"
  | "waltz"
  | "march"
  | "hiphop80s"
  | "funkySyncopated"
  | "weWillRockYou"
  | "billieJean"
  | "backInBlack";
// Settings a starter groove changes before its pattern is loaded. Settings
// left out keep their current values.
export const GROOVE_PRESETS: {
  id: GroovePreset;
  label: string;
  meter?: 3 | 4;
  subdivision?: 2 | 4;
  tempo?: number;
  drumKit?: DrumKit;
  hiHatFeel?: HiHatFeel;
}[] = [
  { id: "rock", label: "Classic Rock" },
  { id: "pop", label: "Pop Beat" },
  { id: "dance", label: "Dance Beat" },
  { id: "halfTime", label: "Half-Time Groove" },
  { id: "waltz", label: "Waltz (3/4)", meter: 3 },
  { id: "march", label: "March" },
  {
    id: "hiphop80s",
    label: "80’s Hip Hop",
    meter: 4,
    subdivision: 4,
    tempo: 100,
    hiHatFeel: "even",
  },
  {
    id: "funkySyncopated",
    label: "The Funky Syncopated",
    meter: 4,
    subdivision: 4,
    tempo: 105,
    hiHatFeel: "even",
  },
  {
    id: "weWillRockYou",
    label: "We Will Rock You",
    meter: 4,
    subdivision: 2,
    tempo: 81,
    drumKit: "rock",
    hiHatFeel: "even",
  },
  {
    id: "billieJean",
    label: "Billie Jean",
    meter: 4,
    subdivision: 2,
    tempo: 117,
    drumKit: "rock",
    hiHatFeel: "eighth",
  },
  {
    id: "backInBlack",
    label: "Beats in Black",
    meter: 4,
    subdivision: 4,
    tempo: 92,
    drumKit: "rock",
    hiHatFeel: "eighth",
  },
];

// Returns one measure, or two for Beats in Black
export function starterMeasures(
  preset: GroovePreset,
  meter: 3 | 4,
  subdivision: 2 | 4,
): GroovePattern[] {
  const length = meter * subdivision;
  const emptyMeasure = (): GroovePattern => ({
    kick: Array<boolean>(length).fill(false),
    snare: Array<boolean>(length).fill(false),
    hat: Array<boolean>(length).fill(false),
  });
  const first = emptyMeasure();
  const { kick, snare, hat } = first;
  // Most starters are written in eighth notes, so they work with either note
  // value
  const put = (drum: boolean[], ...indices: number[]) =>
    indices.forEach((i) => {
      const slot = i * (subdivision / 2);
      if (slot < length) drum[slot] = true;
    });
  const fillEighths = (drum: boolean[]) => {
    for (let i = 0; i < length; i += subdivision / 2) drum[i] = true;
  };
  switch (preset) {
    case "rock":
    case "billieJean":
    case "backInBlack":
      put(kick, 0, 4);
      put(snare, 2, 6);
      fillEighths(hat);
      break;
    case "pop":
      put(kick, 0, 3, 4);
      put(snare, 2, 6);
      put(hat, 0, 2, 4, 6);
      break;
    case "dance":
      put(kick, 0, 2, 4, 6);
      put(snare, 2, 6);
      put(hat, 1, 3, 5, 7);
      break;
    case "halfTime":
      put(kick, 0, 3, 4);
      put(snare, meter === 4 ? 4 : 2);
      fillEighths(hat);
      break;
    case "waltz":
      put(kick, 0);
      put(snare, 3);
      fillEighths(hat);
      break;
    case "march":
      put(kick, 0, 4);
      put(snare, 2, 3, 6, 7);
      put(hat, 0, 2, 4, 6);
      break;
    case "weWillRockYou":
      put(kick, 0, 1, 4, 5);
      put(snare, 2, 6);
      break;
    // These two are written in sixteenth notes
    case "funkySyncopated":
      [0, 5].forEach((i) => (kick[i] = true));
      [3, 7, 12, 14].forEach((i) => (snare[i] = true));
      [0, 4, 8, 12].forEach((i) => (hat[i] = true));
      break;
    case "hiphop80s":
      [0, 5, 6, 8].forEach((i) => (kick[i] = true));
      [4, 12].forEach((i) => (snare[i] = true));
      [0, 1, 2, 3, 4, 6, 8, 10, 12, 14].forEach((i) => (hat[i] = true));
      break;
  }
  if (preset !== "backInBlack") return [first];
  const second = emptyMeasure();
  [0, 5, 8, 11].forEach((i) => (second.kick[i] = true));
  [4, 7, 10, 13, 14].forEach((i) => (second.snare[i] = true));
  [0, 2, 5, 8, 11].forEach((i) => (second.hat[i] = true));
  return [first, second];
}
